import { useMutation, useQuery } from '@apollo/client'
import { ErrorComponent, ProgressSpinner } from '@medical/components'
import ClinicNameRender from '@medical/components/ClinicNameRender'
import {
  STAFF_CREATE_AS_GROUP,
  STAFF_CREATE_SCHEDULE_AVAILABLE_SHIFTS,
  STAFF_DELETE_AS_GROUP,
  STAFF_DELETE_SCHEDULE_AVAILABLE_SHIFTS,
} from '@medical/constant/permissions'
import { checkStaffPermission, clinicalDepartmentNames } from '@medical/libs'
import Auth from '@medical/middleware/auth'
import { DEPARTMENTS_LIST } from '@medical/pages/staff/CreateAvailableShift/CreateAvailableShift.graphql'
import { useCustom } from '@medical/provider/context'
import downloadCsv from 'download-csv'
import queryString from 'query-string'
import React, { useEffect, useRef, useState } from 'react'

import {
  CHECK_UPLOAD_SCHEDULED_SHIFT_GROUPS,
  CSV_CREATE_FIXED_GROUP,
  CSV_CREATE_RECRUITMENT_GROUP,
  CSV_DELETE_FIXED_GROUP,
  CSV_DELETE_RECRUITMENT_GROUP,
} from './ScheduledShiftGroupCsv.graphql'
import ScheduledShiftGroupCsvScene from './ScheduledShiftGroupCsvScene'
import {
  SCHEDULED_SHIFT_GROUP_CSV_LIMIT,
  runScheduledShiftGroupUpload,
  toCsvRecords,
  unknownCsvColumns,
} from './scheduledShiftGroupCsvUpload'

const ScheduledShiftGroupCsv = ({ location: { search } }) => {
  const isRecruitment = queryString.parse(search).isRecruitment === 'true'
  const [{ i18n, popup }] = useCustom()
  const { loading, error, data } = useQuery(DEPARTMENTS_LIST)
  const [checkUpload] = useMutation(CHECK_UPLOAD_SCHEDULED_SHIFT_GROUPS)
  const [createRecruitmentGroup] = useMutation(CSV_CREATE_RECRUITMENT_GROUP)
  const [createFixedGroup] = useMutation(CSV_CREATE_FIXED_GROUP)
  const [deleteRecruitmentGroup] = useMutation(CSV_DELETE_RECRUITMENT_GROUP)
  const [deleteFixedGroup] = useMutation(CSV_DELETE_FIXED_GROUP)

  const [rows, setRows] = useState([])
  // チェックの結果。行を消したり CSV を読み直したりしたら、チェックからやり直す
  const [checkResult, setCheckResult] = useState(null)
  const [statuses, setStatuses] = useState({})
  const [isBusy, setIsBusy] = useState(false)
  const inFlight = useRef(false)

  const isCreatePermitted = checkStaffPermission({
    functionName: isRecruitment
      ? STAFF_CREATE_SCHEDULE_AVAILABLE_SHIFTS
      : STAFF_CREATE_AS_GROUP,
  })
  const isDeletePermitted = checkStaffPermission({
    functionName: isRecruitment
      ? STAFF_DELETE_SCHEDULE_AVAILABLE_SHIFTS
      : STAFF_DELETE_AS_GROUP,
  })

  // 1グループずつ登録している間に画面を閉じると、そこで止まる。閉じる前に確認を出す
  useEffect(() => {
    if (!isBusy) return undefined
    const onBeforeUnload = event => {
      event.preventDefault()
      // eslint-disable-next-line no-param-reassign
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isBusy])

  if (loading) return <ProgressSpinner />
  if (error) return <ErrorComponent error={error} />

  const resetCheck = () => {
    setCheckResult(null)
    setStatuses({})
  }

  const onLoadRows = loadedRows => {
    resetCheck()
    const unknown = unknownCsvColumns(loadedRows)
    if (unknown.length) {
      popup.error(`CSVに不明な列があります：${unknown.join('、')}`)
      setRows([])
      return
    }
    if (loadedRows.length > SCHEDULED_SHIFT_GROUP_CSV_LIMIT) {
      popup.error(
        `1回にアップロードできるのは${SCHEDULED_SHIFT_GROUP_CSV_LIMIT}行までです。`
      )
      setRows([])
      return
    }
    setRows(loadedRows)
  }

  const onRemoveRow = index => {
    resetCheck()
    setRows(rows.filter((_row, i) => i !== index))
  }

  const onClear = () => {
    resetCheck()
    setRows([])
  }

  const onCheck = async () => {
    if (inFlight.current) return
    inFlight.current = true
    setIsBusy(true)
    try {
      popup.info(i18n.t('staff.uploadCheckCsv.uploading'))
      const {
        data: { staffCheckUploadScheduledShiftGroups },
      } = await checkUpload({
        variables: { records: toCsvRecords(rows), isRecruitment },
      })
      setStatuses({})
      setCheckResult(staffCheckUploadScheduledShiftGroups)
      popup.clear()
      popup.success(i18n.t('staff.uploadCheckCsv.submissionSuccess'))
    } catch (e) {
      popup.clear()
      popup.error(e)
    } finally {
      inFlight.current = false
      setIsBusy(false)
    }
  }

  const onUpload = async () => {
    if (inFlight.current || !checkResult) return
    inFlight.current = true
    setIsBusy(true)
    try {
      const { failed } = await runScheduledShiftGroupUpload({
        groups: checkResult.groups,
        statuses,
        createGroup: variables =>
          isRecruitment
            ? createRecruitmentGroup({ variables })
            : createFixedGroup({ variables }),
        deleteGroup: scheduledAvailableShiftGroupId =>
          (isRecruitment ? deleteRecruitmentGroup : deleteFixedGroup)({
            variables: { scheduledAvailableShiftGroupId },
          }),
        onProgress: setStatuses,
      })
      if (failed) {
        popup.error(
          `「${failed.name}」の登録で止まりました。内容を確認して、もう一度「CSVアップロード」を押すと続きから登録します。`
        )
      } else {
        popup.success(i18n.t('staff.uploadCsv.submissionSuccess'))
      }
    } finally {
      inFlight.current = false
      setIsBusy(false)
    }
  }

  const { clinicalDepartments, departments } = data
  const departmentNames = clinicalDepartmentNames({ departments })
  const downloadClinicalDepartments = () => {
    const sortedDepartments = clinicalDepartments
      .filter(department => department.isDisplay)
      .sort(
        (a, b) =>
          parseInt(a.departmentCode, 10) - parseInt(b.departmentCode, 10)
      )
    downloadCsv(
      sortedDepartments.map(department => ({
        clinicName: `${ClinicNameRender({ clinicalDepartment: department })}`,
        departmentName: departmentNames[department.name],
        departmentCode: department.departmentCode,
      })),
      {
        departmentName: i18n.t('main.departmentName'),
        clinicName: i18n.t('main.clinicName'),
        departmentCode: i18n.t('main.departmentCode'),
      },
      '診療科コード一覧.csv'
    )
  }

  const downloadSample = () => {
    const fileName = isRecruitment
      ? '定期募集シフトCSVアップロードサンプル.csv'
      : '定期確定シフトCSVアップロードサンプル.csv'
    window.location.href = `${
      process.env.REACT_APP_ENDPOINT
    }/files/${fileName}?token=${Auth.getToken()}`
  }

  return (
    <ScheduledShiftGroupCsvScene
      i18n={i18n}
      isRecruitment={isRecruitment}
      isPermitted={isCreatePermitted || isDeletePermitted}
      rows={rows}
      checkResult={checkResult}
      statuses={statuses}
      isBusy={isBusy}
      onLoadRows={onLoadRows}
      onRemoveRow={onRemoveRow}
      onClear={onClear}
      onCheck={onCheck}
      onUpload={onUpload}
      downloadClinicalDepartments={downloadClinicalDepartments}
      downloadSample={downloadSample}
    />
  )
}

export default ScheduledShiftGroupCsv
